import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { quoteApi } from "src/Api/quoteApi";
import { LoadingStage } from "src/Interfaces/shared";
import { RootState } from ".";
import _ from "lodash";
import {
    IAddItemToExistingQuote,
    IAddQuoteCustomers,
    IQuote,
    IQuoteCustomers,
    IQuotesState,
    IUpdateQuoteCustomers,
    IAddItemToNewQuote,
} from "@interfaces";

import { ErrorToast } from "src/Components/toasts/ErrorToast";

const initialState: IQuotesState = {
    quotes: [],
    allQuotes: [],
    loadingStage: LoadingStage.NOT_INITIALIZED,
    quotesDetails: [],
    quotesPreview: null,
    quotesCustomers: {},
    sharedQuotes: [],
};

export const loadMyQuotes = createAsyncThunk<IQuote[]>(
    "myQuotes/loadMyQuotes",
    async (_, { rejectWithValue }) => {
        try {
            const response = await quoteApi.getMyQuotes();
            return response.data.data;
        } catch (error) {
            console.error("Failed to load quotes:", error);
            return rejectWithValue("Failed to load quotes");
        }
    },
);

export const loadMyQuoteCustomers = createAsyncThunk<IQuoteCustomers, string>(
    "myQuotes/loadMyQuoteCustomers",
    async (quoteId, { rejectWithValue }) => {
        try {
            const getCustomersResponse = await quoteApi.getCustomers(quoteId);
            return {
                quoteId,
                customers: getCustomersResponse.data.data,
            };
        } catch (error) {
            console.error(`Failed to load quote customers ${quoteId}:`, error);
            return rejectWithValue("Failed to load quote customers");
        }
    },
);
export const addMyQuoteCustomersAndReload = createAsyncThunk<IQuoteCustomers, IAddQuoteCustomers>(
    "myQuotes/addMyQuoteCustomersAndReload",
    async (loadQuoteCustomers, { rejectWithValue }) => {
        try {
            await quoteApi.addCustomers(loadQuoteCustomers.quoteId, loadQuoteCustomers.customers);
            const getCustomersResponse = await quoteApi.getCustomers(loadQuoteCustomers.quoteId);
            return {
                quoteId: loadQuoteCustomers.quoteId,
                customers: getCustomersResponse.data.data,
            };
        } catch (error: any) {
            console.error(error);
            return rejectWithValue(error.response?.data || error.message);
        }
    },
);

export const updateMyQuoteCustomersAndReload = createAsyncThunk<
    IQuoteCustomers,
    IUpdateQuoteCustomers
>("myQuotes/updateMyQuoteCustomersAndReload", async (loadQuoteCustomers, { rejectWithValue }) => {
    try {
        await quoteApi.setCustomers(loadQuoteCustomers.quoteId, loadQuoteCustomers.customers);
        const getCustomersResponse = await quoteApi.getCustomers(loadQuoteCustomers.quoteId);
        return {
            quoteId: loadQuoteCustomers.quoteId,
            customers: getCustomersResponse.data.data,
        };
    } catch (error) {
        console.error(error);
        return rejectWithValue(`Failed to update qoute customers`);
    }
});

// Async thunk for adding a quote and fetching updated quotes
export const createNewMyQuoteAndReload = createAsyncThunk<IQuote[], IAddItemToNewQuote>(
    "myQuotes/createNewMyQuoteAndReload",
    async (newQuoteItem, { rejectWithValue, getState }) => {
        try {
            const state = getState() as RootState;
            const existingQuotes = state.allQuotes.quotes;
            const isDuplicate = existingQuotes.some(
                (quote) => quote.title.toLowerCase() === newQuoteItem.quoteTitle.toLowerCase(),
            );

            if (isDuplicate) {
                ErrorToast({ Message: `Quote "${newQuoteItem.quoteTitle}" already exists` });
                return rejectWithValue(`Quote "${newQuoteItem.quoteTitle}" already exists`);
            }

            await quoteApi.newQuote(newQuoteItem);
            const response = await quoteApi.getMyQuotes();
            return response.data.data;
        } catch (error) {
            console.error("Failed to add item to cart or fetch updated cart:", error);
            return rejectWithValue("Unable to add item to cart and fetch updated cart data.");
        }
    },
);
// Async thunk for adding an item to an existing quote
export const addItemToExistingMyQuoteAndReload = createAsyncThunk<
    IQuote[],
    IAddItemToExistingQuote
>("myQuotes/addItemToExistingMyQuoteAndReload", async (addQuoteItem, { rejectWithValue }) => {
    try {
        // Make the PUT request with the required data
        await quoteApi.addQuoteItem(addQuoteItem.quoteId, addQuoteItem);

        // Fetch the updated quotes after adding the item
        const response = await quoteApi.getMyQuotes();
        return response.data?.data; // Return the updated quotes list
    } catch (error: any) {
        console.error("Error adding item to existing quote:", error);
        return rejectWithValue(
            error.response?.data || error.message || " Unable to add item to existing quote.",
        );
    }
});

export const deleteMyQuoteAndReload = createAsyncThunk<IQuote[], string>(
    "myQuotes/deleteMyQuoteAndReload",
    async (quoteId, { rejectWithValue }) => {
        try {
            // Make the PUT request with the required data
            await quoteApi.deleteQuote(quoteId);

            // Fetch the updated quotes after adding the item
            const response = await quoteApi.getMyQuotes();
            return response.data?.data; // Return the updated quotes list
        } catch (error) {
            console.error(`Error deleting quote ${quoteId}:`, error);
            return rejectWithValue("Failed to delete quote");
        }
    },
);

const quotesSlice = createSlice({
    name: "myQuotes",
    initialState,
    reducers: {
        resetMyQuote: () => initialState, // Reset state action
        loadMyQuoteData: (state, action: PayloadAction<string>) => {
            const existingQuote = state.quotes.find((q) => q.id === action.payload);
            if (!_.isNil(existingQuote)) {
                // load from cached list
                state.quotesDetails = existingQuote.items;
            }
            const existingQuoteCustomers = state.quotesCustomers[action.payload];
            if (!_.isNil(existingQuoteCustomers)) {
                // load from cached list
                state.quotesCustomers[action.payload] = existingQuoteCustomers;
            }
            loadMyQuoteCustomers(action.payload);
        },
    },
    extraReducers: (builder) => {
        builder
            // Handle fetchQuote actions
            .addCase(loadMyQuotes.pending, (state) => {
                state.loadingStage = LoadingStage.IS_LOADING;
            })
            .addCase(loadMyQuotes.fulfilled, (state, action) => {
                state.quotes = action.payload;
                state.loadingStage = LoadingStage.LOADING_COMPLETE;
                // recalculateTotals(state);
            })
            .addCase(loadMyQuotes.rejected, (state, action) => {
                state.loadingStage = LoadingStage.LOADING_COMPLETE;
                console.error("Error fetching fetchQuote:", action.payload);
            })
            // Handle addToQuoteAndFetch actions
            .addCase(createNewMyQuoteAndReload.rejected, (_, action) => {
                console.error("Error adding to quote:", action.payload);
            })
            // Handle addItemToExistingQuote actions
            .addCase(addItemToExistingMyQuoteAndReload.rejected, (_, action) => {
                console.error("Error adding item to existing quote:", action.payload);
            })

            .addCase(loadMyQuoteCustomers.fulfilled, (state, action) => {
                state.quotesCustomers[action.payload.quoteId] = action.payload;
            })
            .addCase(loadMyQuoteCustomers.rejected, (_, action) => {
                console.error("Error loading quote customers:", action.payload);
            })

            .addCase(updateMyQuoteCustomersAndReload.fulfilled, (state, action) => {
                state.quotesCustomers[action.payload.quoteId] = action.payload;
            })
            .addCase(addMyQuoteCustomersAndReload.fulfilled, (state, action) => {
                state.quotesCustomers[action.payload.quoteId] = action.payload;
            });
    },
});
export const selectMyQuotes = (state: RootState) => state.allQuotes;

export const { resetMyQuote, loadMyQuoteData } = quotesSlice.actions;
export default quotesSlice.reducer;
